import { supabase } from '@/lib/supabase';
import { fromPostgrest, guard, raise, type Result } from '@/lib/result';
import { parseIsoDate, todayIso } from '@/shared/lib/dates';
import { fetchPrayerTimes } from '@/shared/lib/prayer-times';

import { mapCandidate, type CandidateProvider } from '../model/candidate';
import { mapCoverageApplication, type CoverageApplication } from '../model/coverage-application';
import { mapMatchedRequest, type MatchedRequest } from '../model/matched-request';
import {
  mapCoverageRequest,
  toRequestRow,
  type CoverageRequest,
  type CoverageRequestCreateInput,
} from '../model/coverage-request';

const REQUEST_WITH_MASJID = '*, masjids(name, city, latitude, longitude)';
const APPLICATION_WITH_PROVIDER = '*, profiles(full_name, city)';
const APPLICATION_WITH_REQUEST =
  '*, coverage_requests(*, masjids(name, city, latitude, longitude))';

export const coverageRepository = {
  async listOpen(): Promise<Result<readonly CoverageRequest[]>> {
    return guard(async () => {
      const { data, error } = await supabase
        .from('coverage_requests')
        .select(REQUEST_WITH_MASJID)
        .eq('status', 'open')
        .gte('end_date', todayIso())
        .order('start_date', { ascending: true });
      if (error) throw fromPostgrest(error);
      return ((data ?? []) as unknown[]).map(mapCoverageRequest);
    });
  },

  async listByCreator(createdBy: string): Promise<Result<readonly CoverageRequest[]>> {
    return guard(async () => {
      const { data, error } = await supabase
        .from('coverage_requests')
        .select(REQUEST_WITH_MASJID)
        .eq('created_by', createdBy)
        .order('created_at', { ascending: false });
      if (error) throw fromPostgrest(error);
      return ((data ?? []) as unknown[]).map(mapCoverageRequest);
    });
  },

  async getById(id: string): Promise<Result<CoverageRequest>> {
    return guard(async () => {
      const { data, error } = await supabase
        .from('coverage_requests')
        .select(REQUEST_WITH_MASJID)
        .eq('id', id)
        .single();
      if (error) throw fromPostgrest(error);
      return mapCoverageRequest(data as unknown);
    });
  },

  async create(input: CoverageRequestCreateInput): Promise<Result<CoverageRequest>> {
    return guard(async () => {
      const { data, error } = await supabase
        .from('coverage_requests')
        .insert(toRequestRow(input))
        .select(REQUEST_WITH_MASJID)
        .single();
      if (error) throw fromPostgrest(error);
      return mapCoverageRequest(data as unknown);
    });
  },

  async cancel(id: string): Promise<Result<void>> {
    return guard(async () => {
      const { error } = await supabase
        .from('coverage_requests')
        .update({ status: 'cancelled' })
        .eq('id', id);
      if (error) throw fromPostgrest(error);
    });
  },

  async myApplicationForRequest(
    requestId: string,
    providerId: string,
  ): Promise<Result<CoverageApplication | null>> {
    return guard(async () => {
      const { data, error } = await supabase
        .from('coverage_applications')
        .select('*')
        .eq('request_id', requestId)
        .eq('provider_id', providerId)
        .maybeSingle();
      if (error) throw fromPostgrest(error);
      return data ? mapCoverageApplication(data as unknown) : null;
    });
  },

  async apply(
    requestId: string,
    providerId: string,
    message: string | null,
  ): Promise<Result<CoverageApplication>> {
    return guard(async () => {
      const { data, error } = await supabase
        .from('coverage_applications')
        .insert({ request_id: requestId, provider_id: providerId, message })
        .select('*')
        .single();
      if (error) throw fromPostgrest(error);
      return mapCoverageApplication(data as unknown);
    });
  },

  async listMyApplications(providerId: string): Promise<Result<readonly CoverageApplication[]>> {
    return guard(async () => {
      const { data, error } = await supabase
        .from('coverage_applications')
        .select(APPLICATION_WITH_REQUEST)
        .eq('provider_id', providerId)
        .order('created_at', { ascending: false });
      if (error) throw fromPostgrest(error);
      return ((data ?? []) as unknown[]).map(mapCoverageApplication);
    });
  },

  async listApplicationsForRequest(
    requestId: string,
  ): Promise<Result<readonly CoverageApplication[]>> {
    return guard(async () => {
      const { data, error } = await supabase
        .from('coverage_applications')
        .select(APPLICATION_WITH_PROVIDER)
        .eq('request_id', requestId)
        .order('created_at', { ascending: true });
      if (error) throw fromPostgrest(error);
      return ((data ?? []) as unknown[]).map(mapCoverageApplication);
    });
  },

  async accept(applicationId: string): Promise<Result<void>> {
    return guard(async () => {
      const { error } = await supabase.rpc('accept_application', {
        p_application_id: applicationId,
      });
      if (error) raise('server', error.message, error);
    });
  },

  /** Phase 2: ranked open requests near the signed-in provider's service area. */
  async matchedRequests(radiusKm?: number): Promise<Result<readonly MatchedRequest[]>> {
    return guard(async () => {
      const { data, error } = await supabase.rpc('match_requests_for_provider', {
        p_radius_km: radiusKm ?? null,
      });
      if (error) throw fromPostgrest(error);
      return ((data ?? []) as unknown[]).map(mapMatchedRequest);
    });
  },

  /** Phase 2: ranked candidate providers for one of the caller's requests. */
  async candidatesForRequest(
    requestId: string,
    radiusKm?: number,
  ): Promise<Result<readonly CandidateProvider[]>> {
    return guard(async () => {
      const { data, error } = await supabase.rpc('match_providers_for_request', {
        p_request_id: requestId,
        p_radius_km: radiusKm ?? null,
      });
      if (error) throw fromPostgrest(error);
      return ((data ?? []) as unknown[]).map(mapCandidate);
    });
  },

  /** Phase 3: the request owner marks the accepted application completed/no_show. */
  async completeApplication(
    applicationId: string,
    outcome: 'completed' | 'no_show',
  ): Promise<Result<void>> {
    return guard(async () => {
      const { error } = await supabase.rpc('complete_application', {
        p_application_id: applicationId,
        p_outcome: outcome,
      });
      if (error) raise('server', error.message, error);
    });
  },

  /** Phase 3: server-verified GPS check-in / check-out. Returns verification result. */
  async recordAttendance(
    applicationId: string,
    lat: number,
    lng: number,
    kind: 'in' | 'out',
  ): Promise<Result<{ verified: boolean; distanceM: number | null }>> {
    return guard(async () => {
      const { data, error } = await supabase.rpc('record_attendance', {
        p_application_id: applicationId,
        p_lat: lat,
        p_lng: lng,
        p_kind: kind,
      });
      if (error) raise('server', error.message, error);
      const row = ((data ?? []) as { verified: boolean; distance_m: number | null }[])[0];
      return { verified: row?.verified ?? false, distanceM: row?.distance_m ?? null };
    });
  },

  /**
   * Fixes "prayer times not synced": fetches AlAdhan times for the masjid's
   * location on the given date and upserts them into the server-side cache the
   * check-in RPC reads. Restricted server-side to the masjid owner/manager (never
   * the provider being checked in — see the migration for why).
   */
  async syncPrayerTimesForDate(
    masjidId: string,
    lat: number,
    lng: number,
    dateIso: string,
  ): Promise<Result<void>> {
    return guard(async () => {
      const date = parseIsoDate(dateIso);
      const times = await fetchPrayerTimes(
        lat,
        lng,
        date.getFullYear(),
        date.getMonth() + 1,
        date.getDate(),
      );
      const { error } = await supabase.rpc('upsert_prayer_times', {
        p_masjid_id: masjidId,
        p_date: dateIso,
        p_fajr: times.fajr.toISOString(),
        p_dhuhr: times.dhuhr.toISOString(),
        p_asr: times.asr.toISOString(),
        p_maghrib: times.maghrib.toISOString(),
        p_isha: times.isha.toISOString(),
        p_jumuah: times.jumuah.toISOString(),
      });
      if (error) raise('server', error.message, error);
    });
  },

  async withdraw(applicationId: string): Promise<Result<void>> {
    return guard(async () => {
      const { error } = await supabase
        .from('coverage_applications')
        .update({ status: 'withdrawn' })
        .eq('id', applicationId);
      if (error) throw fromPostgrest(error);
    });
  },
};
